import React, { FC } from 'react';
import { Circle, Ellipse, G, Line, Svg } from 'react-native-svg';

interface BloomFlowerProps {
  size?: number;
  /** 0..1 — how much the flower blooms (assessment value) */
  bloom: number;
  petalColor: string;
  rayColor: string;
  coreColor?: string;
}

/**
 * The assessment frame's flower: soft petals + thin rays around a warm core.
 * The petals and rays open with the bloom value so the flower mirrors the
 * user's answer, as in the design.
 */
export const BloomFlower: FC<BloomFlowerProps> = ({
  size = 220,
  bloom,
  petalColor,
  rayColor,
  coreColor = '#F3CB56',
}) => {
  const c = size / 2;
  const petalCount = 8;
  const rayCount = 24;
  // fully bloomed, the petal tips reach core + petal ≈ 0.54×inner: computing
  // on a padded inner size keeps them inside the canvas instead of cropping
  const inner = size * 0.9;
  const petalLength = inner * (0.22 + 0.2 * bloom);
  const petalWidth = inner * (0.13 + 0.05 * bloom);
  const rayLength = inner * (0.3 + 0.18 * bloom);
  const coreRadius = inner * (0.09 + 0.03 * bloom);

  const petals = Array.from({ length: petalCount }, (unused, i) => (360 / petalCount) * i);
  const rays = Array.from({ length: rayCount }, (unused, i) => (360 / rayCount) * i + 7.5);

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <G>
        {rays.map(angle => (
          <G key={`ray-${angle}`} transform={`rotate(${angle} ${c} ${c})`}>
            <Line
              x1={c}
              y1={c - coreRadius - 2}
              x2={c}
              y2={c - rayLength}
              stroke={rayColor}
              strokeWidth={1.5}
              strokeLinecap="round"
              opacity={0.35 + 0.35 * bloom}
            />
          </G>
        ))}
        {petals.map(angle => (
          <G key={`petal-${angle}`} transform={`rotate(${angle} ${c} ${c})`}>
            <Ellipse
              cx={c}
              cy={c - coreRadius - petalLength / 2}
              rx={petalWidth}
              ry={petalLength / 2}
              fill={petalColor}
              opacity={0.35 + 0.4 * bloom}
            />
          </G>
        ))}
        <Circle cx={c} cy={c} r={coreRadius + 4} fill={coreColor} opacity={0.35} />
        <Circle cx={c} cy={c} r={coreRadius} fill={coreColor} opacity={0.9} />
      </G>
    </Svg>
  );
};
